import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  usePostSalesRecipient,
  usePatchSalesRecipient
} from '../../../api/sales-commission-incentive'
import { getErrorMessage } from '../../../api/utils'
import { useSnackbar } from 'notistack'

const schema = yup.object().shape({
  name: yup.string().required('Name is required').trim(),
  employeeCode: yup.string().required('Employee code is required').trim(),
  email: yup.string().email('Invalid email').required('Email is required').trim(),
  salesIds: yup.array().of(yup.string()).min(1, 'At least one Sales ID is required')
})

const defaultValues = { name: '', employeeCode: '', email: '', salesIds: [] }

const RecipientDialog = ({ open, setOpen, data, refetch, allSalesIds = [], isLoadingSalesIds }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: createRecipient } = usePostSalesRecipient()
  const { mutateAsync: updateRecipient } = usePatchSalesRecipient(data?._id)

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({ resolver: yupResolver(schema) })

  const onClose = () => {
    setOpen(false)
    reset()
  }

  const onSubmit = async (values) => {
    try {
      const isEdit = Boolean(data)

      await (isEdit ? updateRecipient(values) : createRecipient(values))

      enqueueSnackbar(`Recipient ${isEdit ? 'updated' : 'created'}`, { variant: 'success' })

      refetch()
      onClose()
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error', style: { whiteSpace: 'pre-line' } })
    }
  }

  useEffect(
    () => reset(data ? { ...defaultValues, ...data } : { ...defaultValues }),
    [data, reset, open]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{data ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='name'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Name'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='employeeCode'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Employee Code'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='email'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Email'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='salesIds'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    multiple
                    options={allSalesIds}
                    value={field.value}
                    loading={isLoadingSalesIds}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Sales IDs'
                        placeholder='Select or type Sales ID'
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isSubmitting}>
            {data ? 'UPDATE' : 'CREATE'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default RecipientDialog
